import React from 'react';
import { ArrowLeft, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import type { SelectedFeatures } from '../../data/pricing';
import { pricingData, calculateTotalCost } from '../../data/pricing';

interface BookingCalendarProps {
  selectedFeatures: SelectedFeatures[];
  onBack: () => void;
}

export function BookingCalendar({ selectedFeatures, onBack }: BookingCalendarProps) {
  const totals = selectedFeatures.reduce((acc, selected) => {
    const costs = calculateTotalCost(selected);
    return {
      setup: acc.setup + costs.setup,
      monthly: acc.monthly + costs.monthly
    };
  }, { setup: 0, monthly: 0 });

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-green-500 hover:text-green-400 mb-8 group"
      >
        <ArrowLeft className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" />
        Back to Services
      </button>

      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-white mb-4">Your Quote Summary</h2>
        <p className="text-xl text-gray-400">
          We've received your quote request. A new tab has opened for you to schedule your consultation.
        </p>
      </div>

      <div className="bg-black/50 border border-green-500/20 rounded-lg p-8 mb-12">
        <h3 className="text-2xl font-bold text-white mb-6">Selected Services Summary</h3>
        <div className="space-y-4">
          {selectedFeatures.map((selected) => {
            const service = pricingData[selected.serviceId];
            const costs = calculateTotalCost(selected);
            
            return (
              <div key={selected.serviceId} className="flex justify-between items-start border-b border-green-500/10 pb-4">
                <div>
                  <h4 className="text-lg font-bold text-white">{service.name}</h4>
                  <p className="text-gray-400">{selected.model}</p>
                  <div className="text-sm text-gray-500 mt-2">
                    Selected features:
                    <ul className="ml-4">
                      {Array.from(selected.features).map(featureId => (
                        <li key={featureId}>• {service.features[featureId].name}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold">
                    ${costs.setup}
                  </div>
                  {costs.monthly > 0 && (
                    <div className="text-gray-400">
                      + ${costs.monthly}/mo
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center border-t border-green-500/20 pt-4 mt-4">
          <div>
            <div className="text-lg font-bold text-white">Total Setup Cost</div>
            {totals.monthly > 0 && (
              <div className="text-gray-400">Total Monthly Cost</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-white">${totals.setup}</div>
            {totals.monthly > 0 && (
              <div className="text-gray-400">${totals.monthly}/mo</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-black/50 border border-green-500/20 rounded-lg p-8 mb-12">
        <h3 className="text-2xl font-bold text-white mb-6">What to Expect</h3>
        <div className="space-y-6">
          <div>
            <h4 className="flex items-center gap-2 text-lg font-bold text-white mb-4">
              <Calendar className="w-5 h-5 text-green-500" />
              During the Consultation
            </h4>
            <ul className="space-y-3">
              {[
                'Detailed discussion of your requirements',
                'Technical implementation planning',
                'Timeline and milestone review',
                'Integration requirements analysis',
                'Custom solution recommendations'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-400">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="flex items-center gap-2 text-lg font-bold text-white mb-4">
              <Clock className="w-5 h-5 text-green-500" />
              Next Steps
            </h4>
            <ul className="space-y-3">
              {[
                'Receive detailed proposal',
                'Review implementation plan',
                'Discuss customization options',
                'Finalize agreement terms',
                'Begin implementation'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-400">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="text-center space-y-6">
        <div className="flex items-start gap-2 max-w-xl mx-auto text-gray-400 text-sm">
          <AlertCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
          <p>
            The consultation is completely free and there's no obligation to proceed. 
            We'll discuss your needs, answer any questions, and provide detailed pricing 
            and implementation information.
          </p>
        </div>

        <button
          onClick={() => window.open('https://calendly.com/priorityai-info/ai-booking-demo', '_blank', 'noopener,noreferrer')}
          className="px-8 py-4 bg-green-500 text-black font-bold rounded-md 
            hover:bg-green-400 transition-all transform hover:scale-105
            shadow-lg shadow-green-500/20 hover:shadow-green-500/30"
        >
          Schedule Your Free Consultation
        </button>
      </div>
    </div>
  );
}